import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, MagnifyingGlass, Eye, X, CurrencyInr, Printer } from '@phosphor-icons/react';
import { getPayments, createPayment, getInvoices } from '../../utils/api';
import { formatPrice, formatDate } from '../../utils/helpers';
import { toast } from 'sonner';
import PaymentReceiptView from '../../components/admin/PaymentReceiptView';

const AdminPayments = () => {
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const receiptRef = useRef(null);

  const handlePrintReceipt = () => {
    const printContent = receiptRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Payment Receipt</title>
          <style>
            body { margin: 0; padding: 20px; }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const [formData, setFormData] = useState({
    invoice_id: '',
    amount: '',
    payment_method: 'cash',
    payment_date: new Date().toISOString().split('T')[0],
    reference_number: '',
    notes: ''
  });

  const paymentMethods = [
    { value: 'cash', label: 'Cash' },
    { value: 'card', label: 'Card' },
    { value: 'upi', label: 'UPI' },
    { value: 'bank_transfer', label: 'Bank Transfer' }
  ];

  const fetchPendingInvoices = async () => {
    try {
      const res = await getInvoices({ limit: 100, status: 'pending' });
      const partialRes = await getInvoices({ limit: 100, status: 'partial' });
      setPendingInvoices([...(res.data.data || []), ...(partialRes.data.data || [])]);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: 10,
      };
      if (search) params.search = search;
      if (methodFilter) params.method = methodFilter;
      
      const res = await getPayments(params);
      setPayments(res.data.data || []);
      setPagination(prev => ({
        ...prev,
        totalPages: res.data.pagination?.total_pages || 1,
        total: res.data.pagination?.total || 0
      }));
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingInvoices();
  }, []);

  useEffect(() => {
    fetchPayments();
  }, [pagination.page, search, methodFilter]);

  const resetForm = () => {
    setFormData({
      invoice_id: '',
      amount: '',
      payment_method: 'cash',
      payment_date: new Date().toISOString().split('T')[0],
      reference_number: '',
      notes: ''
    });
  };

  const openCreateModal = () => {
    resetForm();
    fetchPendingInvoices();
    setShowCreateModal(true);
  };

  const handleInvoiceSelect = (invoiceId) => {
    setFormData({ ...formData, invoice_id: invoiceId });
    const invoice = pendingInvoices.find(inv => inv.id === parseInt(invoiceId));
    if (invoice) {
      setFormData(prev => ({ ...prev, amount: invoice.balance_amount || invoice.total_amount - invoice.paid_amount }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const paymentData = {
        invoice_id: parseInt(formData.invoice_id),
        amount: parseFloat(formData.amount),
        payment_method: formData.payment_method,
        payment_date: formData.payment_date || null,
        reference_number: formData.reference_number || null,
        notes: formData.notes || null
      };

      await createPayment(paymentData);
      toast.success('Payment recorded successfully');
      setShowCreateModal(false);
      resetForm();
      fetchPayments();
      fetchPendingInvoices();
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewPayment = (payment) => {
    setSelectedPayment(payment);
    setShowModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-display text-2xl text-secondary">Payments / Billing</h1>
          <p className="font-body text-sm text-text-secondary">{pagination.total} total payments</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
          {/* Search */}
          <div className="relative flex-1 sm:w-64">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="Search payments..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPagination(p => ({...p, page: 1})); }}
              data-testid="payments-search"
              className="w-full pl-10 pr-4 py-2 border border-border-default focus:border-primary focus:outline-none font-body text-sm"
            />
          </div>
          
          {/* Method Filter */}
          <select
            value={methodFilter}
            onChange={(e) => { setMethodFilter(e.target.value); setPagination(p => ({...p, page: 1})); }}
            data-testid="method-filter"
            className="px-4 py-2 border border-border-default focus:border-primary focus:outline-none font-body text-sm bg-white"
          >
            <option value="">All Methods</option>
            {paymentMethods.map(method => (
              <option key={method.value} value={method.value}>{method.label}</option>
            ))}
          </select>

          <button
            onClick={openCreateModal}
            data-testid="record-payment-btn"
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={18} />
            Record Payment
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="admin-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded flex items-center justify-center">
              <CurrencyInr size={20} className="text-green-600" />
            </div>
            <div>
              <p className="font-body text-xs text-text-muted">Pending Invoices</p>
              <p className="font-display text-xl text-secondary">{pendingInvoices.length}</p>
            </div>
          </div>
        </div>
        <div className="admin-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded flex items-center justify-center">
              <CurrencyInr size={20} className="text-yellow-600" />
            </div>
            <div>
              <p className="font-body text-xs text-text-muted">Total Pending Amount</p>
              <p className="font-display text-xl text-secondary">
                {formatPrice(pendingInvoices.reduce((sum, inv) => sum + (inv.balance_amount || 0), 0))}
              </p>
            </div>
          </div>
        </div>
        <div className="admin-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
              <CurrencyInr size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="font-body text-xs text-text-muted">Payments This Page</p>
              <p className="font-display text-xl text-secondary">{payments.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="admin-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-default bg-background-subtle">
                <th className="text-left font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Date</th>
                <th className="text-left font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Invoice</th>
                <th className="text-left font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Customer</th>
                <th className="text-right font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Amount</th>
                <th className="text-center font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Method</th>
                <th className="text-right font-body text-xs uppercase tracking-wider text-text-secondary py-4 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="border-b border-border-default">
                    <td colSpan={6} className="py-4 px-4">
                      <div className="h-12 bg-gray-100 animate-pulse rounded" />
                    </td>
                  </tr>
                ))
              ) : payments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center">
                    <p className="font-body text-text-secondary">No payments found</p>
                    <button onClick={openCreateModal} className="btn-secondary mt-4">
                      Record First Payment
                    </button>
                  </td>
                </tr>
              ) : (
                payments.map((payment) => (
                  <motion.tr
                    key={payment.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="border-b border-border-default hover:bg-background-subtle transition-colors"
                    data-testid={`payment-row-${payment.id}`}
                  >
                    <td className="py-4 px-4">
                      <span className="font-body text-sm text-secondary">
                        {formatDate(payment.payment_date)}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="font-body text-sm text-secondary font-medium">
                        {payment.invoice_number}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div>
                        <p className="font-body text-sm text-secondary">{payment.customer_name}</p>
                        <p className="font-body text-xs text-text-muted">{payment.customer_phone || payment.customer_email}</p>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <span className="font-body text-sm text-green-600 font-medium">
                        {formatPrice(payment.amount)}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className="font-body text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 capitalize">
                        {payment.payment_method?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end">
                        <button
                          onClick={() => handleViewPayment(payment)}
                          data-testid={`view-payment-${payment.id}`}
                          className="p-2 text-text-muted hover:text-primary transition-colors"
                          title="View"
                        >
                          <Eye size={18} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4 border-t border-border-default">
            {[...Array(Math.min(pagination.totalPages, 5))].map((_, i) => (
              <button
                key={i}
                onClick={() => setPagination(p => ({ ...p, page: i + 1 }))}
                className={`w-8 h-8 font-body text-sm transition-colors ${
                  pagination.page === i + 1 
                    ? 'bg-primary text-white' 
                    : 'bg-background-subtle text-secondary hover:bg-primary/10'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* View Payment Modal */}
      {showModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white w-full max-w-md"
          >
            <div className="flex items-center justify-between p-4 border-b border-border-default">
              <h3 className="font-display text-xl text-secondary">Payment Details</h3>
              <button onClick={() => setShowModal(false)}>
                <X size={24} className="text-text-muted hover:text-secondary" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="text-center p-4 bg-green-50 rounded">
                <p className="font-body text-xs text-green-600 uppercase mb-1">Amount Paid</p>
                <p className="font-display text-3xl text-green-600">{formatPrice(selectedPayment.amount)}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-body text-xs text-text-muted uppercase">Invoice</p>
                  <p className="font-body text-sm text-secondary">{selectedPayment.invoice_number}</p>
                </div>
                <div>
                  <p className="font-body text-xs text-text-muted uppercase">Customer</p>
                  <p className="font-body text-sm text-secondary">{selectedPayment.customer_name}</p>
                </div>
                <div>
                  <p className="font-body text-xs text-text-muted uppercase">Payment Date</p>
                  <p className="font-body text-sm text-secondary">{formatDate(selectedPayment.payment_date)}</p>
                </div>
                <div>
                  <p className="font-body text-xs text-text-muted uppercase">Method</p>
                  <p className="font-body text-sm text-secondary capitalize">{selectedPayment.payment_method?.replace('_', ' ')}</p>
                </div>
              </div>

              {selectedPayment.reference_number && (
                <div>
                  <p className="font-body text-xs text-text-muted uppercase">Reference Number</p>
                  <p className="font-body text-sm text-secondary">{selectedPayment.reference_number}</p>
                </div>
              )}

              {selectedPayment.notes && (
                <div>
                  <p className="font-body text-xs text-text-muted uppercase">Notes</p>
                  <p className="font-body text-sm text-text-secondary">{selectedPayment.notes}</p>
                </div>
              )}

              {/* Print Receipt Button */}
              <div className="pt-4 border-t border-border-default">
                <button
                  onClick={handlePrintReceipt}
                  data-testid="print-receipt-btn"
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  <Printer size={18} />
                  Print Receipt
                </button>
              </div>

              {/* Hidden Receipt View */}
              <div className="hidden">
                <PaymentReceiptView ref={receiptRef} payment={selectedPayment} />
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Record Payment Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white w-full max-w-lg"
          >
            <div className="flex items-center justify-between p-4 border-b border-border-default">
              <h3 className="font-display text-xl text-secondary">Record Payment</h3>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }}>
                <X size={24} className="text-text-muted hover:text-secondary" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block font-body text-sm text-text-secondary mb-2">Select Invoice *</label>
                <select
                  value={formData.invoice_id}
                  onChange={(e) => handleInvoiceSelect(e.target.value)}
                  required
                  data-testid="payment-invoice"
                  className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body bg-white"
                >
                  <option value="">-- Select Invoice --</option>
                  {pendingInvoices.map((invoice) => (
                    <option key={invoice.id} value={invoice.id}>
                      {invoice.invoice_number} - {invoice.customer_name} - Balance: {formatPrice(invoice.balance_amount || (invoice.total_amount - invoice.paid_amount))}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-body text-sm text-text-secondary mb-2">Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                    data-testid="payment-amount"
                    className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block font-body text-sm text-text-secondary mb-2">Payment Method *</label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                    required
                    data-testid="payment-method"
                    className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body bg-white"
                  >
                    {paymentMethods.map(method => (
                      <option key={method.value} value={method.value}>{method.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-body text-sm text-text-secondary mb-2">Payment Date</label>
                  <input
                    type="date"
                    value={formData.payment_date}
                    onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                    data-testid="payment-date"
                    className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body"
                  />
                </div>
                <div>
                  <label className="block font-body text-sm text-text-secondary mb-2">Reference Number</label>
                  <input
                    type="text"
                    value={formData.reference_number}
                    onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                    data-testid="payment-reference"
                    className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body"
                    placeholder="Transaction ID, etc."
                  />
                </div>
              </div>

              <div>
                <label className="block font-body text-sm text-text-secondary mb-2">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  data-testid="payment-notes"
                  className="w-full px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body resize-none"
                  placeholder="Payment notes..."
                />
              </div>
              
              <div className="flex gap-4 pt-4">
                <button 
                  type="button" 
                  onClick={() => { setShowCreateModal(false); resetForm(); }} 
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={submitting}
                  data-testid="save-payment-btn" 
                  className="btn-primary flex-1 disabled:opacity-50"
                >
                  {submitting ? 'Recording...' : 'Record Payment'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminPayments;
